import { useState, useEffect, useCallback } from 'react';
import { pm } from '../../services/api';
import { theme } from '../../theme';

/* ─── helpers ───────────────────────────────────────────────────── */
const fmt = iso =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—';

const fmtInput = iso => iso ? iso.split('T')[0] : '';

const MODULE_STATUS = {
  backlog:    { label:'Backlog',     color:'#6B7280', bg:'#F3F4F6' },
  in_progress:{ label:'In Progress', color:'#2563EB', bg:'#DBEAFE' },
  paused:     { label:'Paused',      color:'#D97706', bg:'#FEF3C7' },
  completed:  { label:'Completed',   color:'#16A34A', bg:'#DCFCE7' },
  cancelled:  { label:'Cancelled',   color:'#DC2626', bg:'#FEE2E2' },
};

const MODULE_STATUS_OPTIONS = [
  'backlog','in_progress','paused','completed','cancelled',
];

const PRIORITY_META = {
  urgent: { label:'Urgent', color:'#EF4444', symbol:'⚡' },
  high:   { label:'High',   color:'#F97316', symbol:'↑' },
  medium: { label:'Medium', color:'#EAB308', symbol:'→' },
  low:    { label:'Low',    color:'#22C55E', symbol:'↓' },
  none:   { label:'None',   color:'#9CA3AF', symbol:'—' },
};

const TYPE_META = {
  task:        { label:'Task',        color:'#3B82F6', icon:'◈' },
  story:       { label:'Story',       color:'#8B5CF6', icon:'◉' },
  bug:         { label:'Bug',         color:'#EF4444', icon:'⬡' },
  feature:     { label:'Feature',     color:'#14B8A6', icon:'★' },
  improvement: { label:'Improvement', color:'#F59E0B', icon:'▲' },
  test:        { label:'Test',        color:'#6B7280', icon:'✓' },
};

const ACCENTS = ['#F97316','#3B82F6','#8B5CF6','#EC4899','#14B8A6','#F59E0B','#22C55E','#EF4444'];
const accentFor = (str = '') => ACCENTS[(str.charCodeAt(0) || 0) % ACCENTS.length];

function Avatar({ name = '', size = 22 }) {
  const bg = accentFor(name);
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', background:`${bg}20`, color:bg,
                  fontSize: size * 0.42, fontWeight:700, display:'flex',
                  alignItems:'center', justifyContent:'center', flexShrink:0, border:`1.5px solid ${bg}40` }}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

/* ─── ModuleFormModal ───────────────────────────────────────────── */
function ModuleFormModal({ projectId, module, members, onSave, onClose }) {
  const editing = !!module;
  const [form, setForm] = useState({
    name:         module?.name         ?? '',
    description:  module?.description  ?? '',
    status:       module?.status       ?? 'backlog',
    start_date:   fmtInput(module?.start_date),
    target_date:  fmtInput(module?.target_date),
    lead_id:      module?.lead_id      ?? '',
    milestone_id: module?.milestone_id ?? '',
  });
  const [milestones, setMilestones] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  useEffect(() => {
    pm.getMilestones(projectId).then(r => setMilestones(r.data || [])).catch(() => {});
  }, [projectId]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        name:         form.name.trim(),
        description:  form.description.trim() || null,
        status:       form.status,
        start_date:   form.start_date   || null,
        target_date:  form.target_date  || null,
        lead_id:      form.lead_id      || null,
        milestone_id: form.milestone_id || null,
      };
      if (editing) await pm.updateModule(module.id, payload);
      else         await pm.createModule(projectId, payload);
      onSave();
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const inputStyle = {
    width:'100%', padding:'0.45rem 0.65rem', border:'1px solid #E4E4E7',
    borderRadius:7, fontSize:'0.82rem', outline:'none', background:'#fff',
    boxSizing:'border-box',
  };
  const labelStyle = { fontSize:'0.72rem', fontWeight:600, color:'#374151', marginBottom:4, display:'block' };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', display:'flex',
                  alignItems:'center', justifyContent:'center', zIndex:2000 }}
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'#fff', borderRadius:12, width:500, maxWidth:'94vw',
                    boxShadow:'0 8px 40px rgba(0,0,0,0.18)', overflow:'hidden' }}>
        <div style={{ padding:'1rem 1.25rem', borderBottom:'1px solid #F3F4F6',
                      display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ margin:0, fontSize:'0.95rem', fontWeight:700 }}>
            {editing ? 'Edit Module' : 'New Module'}
          </h3>
          <button onClick={onClose}
            style={{ background:'none', border:'none', cursor:'pointer', fontSize:'1.1rem', color:'#9CA3AF' }}>
            ✕
          </button>
        </div>
        <div style={{ padding:'1.25rem', display:'flex', flexDirection:'column', gap:'0.85rem' }}>
          {error && (
            <div style={{ background:'#FEE2E2', color:'#DC2626', padding:'0.5rem 0.75rem',
                          borderRadius:7, fontSize:'0.78rem' }}>{error}</div>
          )}
          <div>
            <label style={labelStyle}>Name *</label>
            <input value={form.name} onChange={set('name')} placeholder="Authentication, Payment Flow…"
                   style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Description</label>
            <textarea value={form.description} onChange={set('description')}
                      placeholder="What does this module cover?" rows={3}
                      style={{ ...inputStyle, resize:'vertical', fontFamily:'inherit' }} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
            <div>
              <label style={labelStyle}>Status</label>
              <select value={form.status} onChange={set('status')} style={inputStyle}>
                {MODULE_STATUS_OPTIONS.map(s => (
                  <option key={s} value={s}>{MODULE_STATUS[s]?.label || s}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Lead</label>
              <select value={form.lead_id} onChange={set('lead_id')} style={inputStyle}>
                <option value="">— No lead —</option>
                {members.map(m => (
                  <option key={m.employee_id} value={m.employee_id}>{m.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
            <div>
              <label style={labelStyle}>Start Date</label>
              <input type="date" value={form.start_date} onChange={set('start_date')} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Target Date</label>
              <input type="date" value={form.target_date} onChange={set('target_date')} style={inputStyle} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Milestone</label>
            <select value={form.milestone_id} onChange={set('milestone_id')} style={inputStyle}>
              <option value="">— No milestone —</option>
              {milestones.map(ms => (
                <option key={ms.id} value={ms.id}>{ms.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ padding:'0.875rem 1.25rem', borderTop:'1px solid #F3F4F6',
                      display:'flex', justifyContent:'flex-end', gap:'0.5rem' }}>
          <button onClick={onClose}
            style={{ padding:'0.45rem 1rem', border:'1px solid #E4E4E7', borderRadius:7,
                     background:'#fff', cursor:'pointer', fontSize:'0.82rem', fontWeight:600, color:'#374151' }}>
            Cancel
          </button>
          <button onClick={submit} disabled={saving}
            style={{ padding:'0.45rem 1.1rem', border:'none', borderRadius:7,
                     background: saving ? '#9CA3AF' : theme.primary,
                     color:'#fff', cursor: saving ? 'not-allowed' : 'pointer',
                     fontSize:'0.82rem', fontWeight:700 }}>
            {saving ? 'Saving…' : (editing ? 'Update' : 'Create Module')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── AddIssuesModal ─────────────────────────────────────────────── */
function AddIssuesModal({ projectId, moduleId, existingIds, onSave, onClose }) {
  const [issues,   setIssues]   = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [search,   setSearch]   = useState('');

  useEffect(() => {
    pm.getIssues(projectId)
      .then(r => setIssues(r.data.filter(i => !existingIds.has(i.id))))
      .finally(() => setLoading(false));
  }, [projectId, existingIds]);

  const toggle = id =>
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const save = async () => {
    if (!selected.length) return;
    setSaving(true);
    await pm.addIssuesToModule(moduleId, { issue_ids: selected }).catch(() => {});
    setSaving(false);
    onSave();
  };

  const filtered = issues.filter(i =>
    !search || i.title.toLowerCase().includes(search.toLowerCase()) ||
    (i.code && i.code.toLowerCase().includes(search.toLowerCase()))
  );

  const tm = k => TYPE_META[k] || TYPE_META.task;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', display:'flex',
                  alignItems:'center', justifyContent:'center', zIndex:2100 }}
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'#fff', borderRadius:12, width:540, maxWidth:'94vw', maxHeight:'80vh',
                    boxShadow:'0 8px 40px rgba(0,0,0,0.18)', display:'flex', flexDirection:'column', overflow:'hidden' }}>
        <div style={{ padding:'1rem 1.25rem', borderBottom:'1px solid #F3F4F6',
                      display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ margin:0, fontSize:'0.95rem', fontWeight:700 }}>Add Issues to Module</h3>
          <button onClick={onClose}
            style={{ background:'none', border:'none', cursor:'pointer', fontSize:'1.1rem', color:'#9CA3AF' }}>
            ✕
          </button>
        </div>
        <div style={{ padding:'0.75rem 1.25rem', borderBottom:'1px solid #F3F4F6' }}>
          <input value={search} onChange={e => setSearch(e.target.value)}
                 placeholder="Search issues…"
                 style={{ width:'100%', padding:'0.45rem 0.65rem', border:'1px solid #E4E4E7',
                          borderRadius:7, fontSize:'0.82rem', outline:'none', boxSizing:'border-box' }} />
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'0.5rem 0' }}>
          {loading ? (
            <div style={{ textAlign:'center', padding:'2rem', color:'#9CA3AF', fontSize:'0.82rem' }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign:'center', padding:'2rem', color:'#9CA3AF', fontSize:'0.82rem' }}>
              No available issues
            </div>
          ) : filtered.map(issue => {
            const checked = selected.includes(issue.id);
            const t = tm(issue.type);
            return (
              <div key={issue.id} onClick={() => toggle(issue.id)}
                style={{ display:'flex', alignItems:'center', gap:'0.625rem',
                         padding:'0.5rem 1.25rem', cursor:'pointer',
                         background: checked ? '#F5F3FF' : 'transparent',
                         borderLeft: checked ? `3px solid ${theme.primary}` : '3px solid transparent' }}
                onMouseEnter={e => { if (!checked) e.currentTarget.style.background='#F9FAFB'; }}
                onMouseLeave={e => { if (!checked) e.currentTarget.style.background='transparent'; }}>
                <div style={{ width:16, height:16, borderRadius:4,
                               border: checked ? `2px solid ${theme.primary}` : '2px solid #D1D5DB',
                               background: checked ? theme.primary : '#fff', display:'flex',
                               alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  {checked && <span style={{ color:'#fff', fontSize:'0.6rem', fontWeight:900 }}>✓</span>}
                </div>
                <span style={{ color:t.color, fontSize:'0.72rem', fontWeight:700, flexShrink:0 }}>{t.icon}</span>
                {issue.code && (
                  <span style={{ color:'#14B8A6', fontFamily:'monospace', fontSize:'0.72rem', fontWeight:600, flexShrink:0 }}>
                    {issue.code}
                  </span>
                )}
                <span style={{ fontSize:'0.82rem', color:'#374151', overflow:'hidden',
                               textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {issue.title}
                </span>
              </div>
            );
          })}
        </div>
        <div style={{ padding:'0.875rem 1.25rem', borderTop:'1px solid #F3F4F6',
                      display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontSize:'0.78rem', color:'#6B7280' }}>{selected.length} selected</span>
          <div style={{ display:'flex', gap:'0.5rem' }}>
            <button onClick={onClose}
              style={{ padding:'0.45rem 1rem', border:'1px solid #E4E4E7', borderRadius:7,
                       background:'#fff', cursor:'pointer', fontSize:'0.82rem', fontWeight:600, color:'#374151' }}>
              Cancel
            </button>
            <button onClick={save} disabled={saving || !selected.length}
              style={{ padding:'0.45rem 1.1rem', border:'none', borderRadius:7,
                       background: (saving || !selected.length) ? '#9CA3AF' : theme.primary,
                       color:'#fff', cursor: (saving || !selected.length) ? 'not-allowed' : 'pointer',
                       fontSize:'0.82rem', fontWeight:700 }}>
              {saving ? 'Adding…' : `Add ${selected.length || ''} Issue${selected.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── ModuleDetail ──────────────────────────────────────────────── */
function ModuleDetail({ projectId, module: moduleProp, members, onBack, onUpdated }) {
  const [issues,   setIssues]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showAdd,  setShowAdd]  = useState(false);
  const [removing, setRemoving] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const module = moduleProp;
  const sm = MODULE_STATUS[module.status] || MODULE_STATUS.backlog;

  const loadIssues = useCallback(() => {
    setLoading(true);
    pm.getModuleIssues(module.id)
      .then(r => setIssues(r.data))
      .finally(() => setLoading(false));
  }, [projectId, module.id]);

  useEffect(() => { loadIssues(); }, [loadIssues]);

  const removeIssue = async (issueId) => {
    setRemoving(issueId);
    await pm.removeIssueFromModule(module.id, issueId).catch(() => {});
    setRemoving(null);
    loadIssues();
    onUpdated();
  };

  const deleteModule = async () => {
    if (!window.confirm('Delete this module? Issues will not be deleted.')) return;
    setDeleting(true);
    await pm.deleteModule(module.id).catch(() => {});
    onBack();
  };

  const existingIds = new Set(issues.map(i => i.id));
  const completed   = issues.filter(i => ['completed','cancelled'].includes(i.state_group)).length;
  const pct         = issues.length ? Math.round(completed / issues.length * 100) : 0;

  const tm  = k => TYPE_META[k] || TYPE_META.task;
  const pm2 = k => PRIORITY_META[k] || PRIORITY_META.none;

  return (
    <>
    {editMode && (
      <ModuleFormModal projectId={projectId} module={module} members={members}
        onSave={() => { setEditMode(false); onUpdated(); }}
        onClose={() => setEditMode(false)} />
    )}
    {showAdd && (
      <AddIssuesModal projectId={projectId} moduleId={module.id} existingIds={existingIds}
        onSave={() => { setShowAdd(false); loadIssues(); onUpdated(); }}
        onClose={() => setShowAdd(false)} />
    )}

    <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
      {/* Back header */}
      <div style={{ background:'#fff', borderBottom:'1px solid #E4E4E7',
                    padding:'0.75rem 1.5rem', display:'flex', alignItems:'center',
                    justifyContent:'space-between', flexWrap:'wrap', gap:'0.5rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.625rem' }}>
          <button onClick={onBack}
            style={{ background:'none', border:'none', cursor:'pointer', color:'#9CA3AF',
                     fontSize:'0.78rem', display:'flex', alignItems:'center', gap:4, padding:0 }}
            onMouseEnter={e => e.currentTarget.style.color = theme.primary}
            onMouseLeave={e => e.currentTarget.style.color = '#9CA3AF'}>
            ← Modules
          </button>
          <span style={{ color:'#D1D5DB' }}>/</span>
          <h2 style={{ margin:0, fontSize:'0.95rem', fontWeight:700, color:'#111827' }}>{module.name}</h2>
          <span style={{ display:'inline-flex', alignItems:'center', gap:4, background:sm.bg,
                         color:sm.color, padding:'2px 8px', borderRadius:5, fontSize:'0.67rem', fontWeight:700 }}>
            <span style={{ width:5, height:5, borderRadius:'50%', background:sm.color }} />
            {sm.label}
          </span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
          <button onClick={() => setEditMode(true)}
            style={{ padding:'0.38rem 0.75rem', border:'1px solid #E4E4E7', borderRadius:7,
                     background:'#fff', cursor:'pointer', fontSize:'0.78rem', fontWeight:600, color:'#374151' }}>
            Edit
          </button>
          <button onClick={deleteModule} disabled={deleting}
            style={{ padding:'0.38rem 0.75rem', border:'1px solid #FEE2E2', borderRadius:7,
                     background:'#fff', cursor: deleting ? 'not-allowed' : 'pointer',
                     fontSize:'0.78rem', fontWeight:600, color:'#EF4444' }}>
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>

      <div style={{ flex:1, overflowY:'auto', padding:'1.25rem 1.5rem',
                    display:'flex', flexDirection:'column', gap:'1.25rem' }}>
        {/* Meta + progress */}
        <div style={{ background:'#fff', borderRadius:10, border:'1px solid #E4E4E7',
                      padding:'1rem 1.25rem', display:'flex', gap:'2rem', flexWrap:'wrap' }}>
          <div style={{ flex:1, minWidth:200 }}>
            {module.description && (
              <p style={{ margin:'0 0 0.75rem', fontSize:'0.82rem', color:'#6B7280', lineHeight:1.5 }}>
                {module.description}
              </p>
            )}
            <div style={{ display:'flex', gap:'1.5rem', flexWrap:'wrap', fontSize:'0.78rem', color:'#6B7280' }}>
              {module.start_date && (
                <div><span style={{ fontWeight:600 }}>Start: </span>{fmt(module.start_date)}</div>
              )}
              {module.target_date && (
                <div><span style={{ fontWeight:600 }}>Target: </span>{fmt(module.target_date)}</div>
              )}
              {module.lead && (
                <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                  <span style={{ fontWeight:600 }}>Lead: </span>
                  <Avatar name={module.lead} size={18} />
                  <span>{module.lead}</span>
                </div>
              )}
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'1.5rem', flexShrink:0 }}>
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:'1.5rem', fontWeight:800, color:'#111827' }}>{issues.length}</div>
              <div style={{ fontSize:'0.72rem', color:'#9CA3AF', fontWeight:600 }}>Issues</div>
            </div>
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:'1.5rem', fontWeight:800, color:'#16A34A' }}>{completed}</div>
              <div style={{ fontSize:'0.72rem', color:'#9CA3AF', fontWeight:600 }}>Done</div>
            </div>
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
              <div style={{ fontSize:'1.5rem', fontWeight:800, color: pct === 100 ? '#16A34A' : '#2563EB' }}>
                {pct}%
              </div>
              <div style={{ width:120, height:6, background:'#E4E4E7', borderRadius:9999, overflow:'hidden' }}>
                <div style={{ width:`${pct}%`, height:'100%',
                              background: pct === 100 ? '#16A34A' : theme.primary,
                              borderRadius:9999, transition:'width 0.4s' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Issues */}
        <div style={{ background:'#fff', borderRadius:10, border:'1px solid #E4E4E7', overflow:'hidden' }}>
          <div style={{ padding:'0.75rem 1rem', borderBottom:'1px solid #F3F4F6',
                        display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:'0.82rem', fontWeight:700, color:'#374151' }}>
              Issues ({issues.length})
            </span>
            <button onClick={() => setShowAdd(true)}
              style={{ padding:'0.35rem 0.85rem', border:'none', borderRadius:6,
                       background:theme.primary, color:'#fff', cursor:'pointer',
                       fontSize:'0.78rem', fontWeight:700 }}>
              + Add Issues
            </button>
          </div>
          {loading ? (
            <div style={{ padding:'2.5rem', textAlign:'center', color:'#9CA3AF', fontSize:'0.82rem' }}>
              Loading issues…
            </div>
          ) : issues.length === 0 ? (
            <div style={{ padding:'3rem', textAlign:'center' }}>
              <div style={{ fontSize:'2rem', marginBottom:'0.5rem' }}>▦</div>
              <p style={{ color:'#6B7280', fontSize:'0.85rem', margin:0 }}>No issues in this module yet.</p>
              <button onClick={() => setShowAdd(true)}
                style={{ marginTop:'0.75rem', padding:'0.45rem 1rem', border:'none', borderRadius:7,
                         background:theme.primary, color:'#fff', cursor:'pointer',
                         fontSize:'0.78rem', fontWeight:700 }}>
                Add Issues
              </button>
            </div>
          ) : issues.map((issue, idx) => {
            const t  = tm(issue.type);
            const p2 = pm2(issue.priority);
            const isDone = ['completed','cancelled'].includes(issue.state_group);
            return (
              <div key={issue.id}
                style={{ display:'flex', alignItems:'center', gap:'0.625rem', padding:'0.6rem 1rem',
                         borderBottom: idx < issues.length - 1 ? '1px solid #F9FAFB' : 'none',
                         background:'#fff' }}
                onMouseEnter={e => e.currentTarget.style.background='#F9FAFB'}
                onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                <span title={t.label} style={{ color:t.color, fontSize:'0.8rem', flexShrink:0 }}>{t.icon}</span>
                {issue.code && (
                  <span style={{ color:'#14B8A6', fontFamily:'monospace', fontSize:'0.72rem',
                                 fontWeight:600, flexShrink:0 }}>{issue.code}</span>
                )}
                <span style={{ flex:1, fontSize:'0.82rem', color: isDone ? '#9CA3AF' : '#374151',
                               textDecoration: isDone ? 'line-through' : 'none',
                               overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {issue.title}
                </span>
                {issue.state_name && (
                  <span style={{ fontSize:'0.67rem', fontWeight:600, padding:'2px 7px', borderRadius:5,
                                 background:'#F3F4F6', color:'#6B7280', flexShrink:0 }}>
                    {issue.state_name}
                  </span>
                )}
                <span title={p2.label} style={{ color:p2.color, fontSize:'0.78rem', fontWeight:700, flexShrink:0 }}>
                  {p2.symbol}
                </span>
                {issue.assignees?.length > 0 && (
                  <div style={{ display:'flex', gap:2, flexShrink:0 }}>
                    {issue.assignees.slice(0,3).map(a => (
                      <Avatar key={a.id} name={a.name} size={20} />
                    ))}
                  </div>
                )}
                <button onClick={() => removeIssue(issue.id)} disabled={removing === issue.id}
                  title="Remove from module"
                  style={{ background:'none', border:'none', cursor: removing === issue.id ? 'not-allowed' : 'pointer',
                           color:'#9CA3AF', fontSize:'0.85rem', padding:'0 4px', flexShrink:0,
                           lineHeight:1, borderRadius:4 }}
                  onMouseEnter={e => e.currentTarget.style.color='#EF4444'}
                  onMouseLeave={e => e.currentTarget.style.color='#9CA3AF'}>
                  {removing === issue.id ? '…' : '✕'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
    </>
  );
}

/* ─── ModuleCard ─────────────────────────────────────────────────── */
function ModuleCard({ module, onOpen, onEdit, onDelete }) {
  const sm  = MODULE_STATUS[module.status] || MODULE_STATUS.backlog;
  const pct = module.completion_pct ?? 0;

  const today    = new Date(); today.setHours(0,0,0,0);
  const dueDate  = module.target_date ? new Date(module.target_date) : null;
  const isOverdue = dueDate && dueDate < today && module.status !== 'completed';

  return (
    <div style={{ background:'#fff', borderRadius:10, border:'1px solid #E4E4E7',
                  overflow:'hidden', transition:'box-shadow 0.12s' }}
      onMouseEnter={e => e.currentTarget.style.boxShadow='0 2px 12px rgba(0,0,0,0.08)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow='none'}>
      <div style={{ padding:'1rem 1.125rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.5rem' }}>
          <div style={{ flex:1, minWidth:0 }}>
            <button onClick={onOpen}
              style={{ background:'none', border:'none', cursor:'pointer', padding:0, textAlign:'left',
                       fontSize:'0.9rem', fontWeight:700, color:'#111827', display:'block',
                       overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'100%' }}
              onMouseEnter={e => e.currentTarget.style.color = theme.primary}
              onMouseLeave={e => e.currentTarget.style.color = '#111827'}>
              ▦ {module.name}
            </button>
            {module.description && (
              <p style={{ margin:'0.2rem 0 0', fontSize:'0.75rem', color:'#9CA3AF',
                          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {module.description}
              </p>
            )}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', flexShrink:0, marginLeft:'0.75rem' }}>
            <span style={{ display:'inline-flex', alignItems:'center', gap:3, background:sm.bg,
                           color:sm.color, padding:'2px 7px', borderRadius:5, fontSize:'0.65rem', fontWeight:700 }}>
              <span style={{ width:5, height:5, borderRadius:'50%', background:sm.color }} />
              {sm.label}
            </span>
            <button onClick={onEdit}
              style={{ background:'none', border:'none', cursor:'pointer', color:'#9CA3AF',
                       fontSize:'0.78rem', padding:'2px 6px', borderRadius:4 }}
              title="Edit"
              onMouseEnter={e => e.currentTarget.style.color='#374151'}
              onMouseLeave={e => e.currentTarget.style.color='#9CA3AF'}>
              ✏
            </button>
            <button onClick={onDelete}
              style={{ background:'none', border:'none', cursor:'pointer', color:'#9CA3AF',
                       fontSize:'0.8rem', padding:'2px 6px', borderRadius:4 }}
              title="Delete"
              onMouseEnter={e => e.currentTarget.style.color='#EF4444'}
              onMouseLeave={e => e.currentTarget.style.color='#9CA3AF'}>
              🗑
            </button>
          </div>
        </div>

        {/* Dates */}
        <div style={{ display:'flex', alignItems:'center', gap:'1rem', fontSize:'0.73rem',
                      color: isOverdue ? '#EF4444' : '#9CA3AF', marginBottom:'0.75rem' }}>
          {module.start_date && <span>📅 {fmt(module.start_date)}</span>}
          {module.start_date && module.target_date && <span style={{ color:'#D1D5DB' }}>→</span>}
          {module.target_date && (
            <span style={{ color: isOverdue ? '#EF4444' : '#9CA3AF', fontWeight: isOverdue ? 700 : 400 }}>
              {fmt(module.target_date)}{isOverdue ? ' · Overdue' : ''}
            </span>
          )}
          {module.lead && (
            <div style={{ display:'flex', alignItems:'center', gap:4, marginLeft:'auto' }}>
              <Avatar name={module.lead} size={16} />
              <span style={{ fontSize:'0.72rem', color:'#6B7280' }}>{module.lead}</span>
            </div>
          )}
        </div>

        {/* Progress */}
        <div style={{ display:'flex', alignItems:'center', gap:'0.625rem', marginBottom:'0.5rem' }}>
          <div style={{ flex:1, height:5, background:'#E4E4E7', borderRadius:9999, overflow:'hidden' }}>
            <div style={{ width:`${pct}%`, height:'100%',
                          background: pct === 100 ? '#16A34A' : theme.primary,
                          borderRadius:9999, transition:'width 0.4s' }} />
          </div>
          <span style={{ fontSize:'0.7rem', fontWeight:700,
                         color: pct === 100 ? '#16A34A' : theme.primary, minWidth:30, textAlign:'right' }}>
            {pct}%
          </span>
        </div>

        {/* Counts */}
        <div style={{ display:'flex', gap:'0.5rem' }}>
          {[
            { v: module.total_issues     ?? 0, label:'Issues',  color:'#2563EB', bg:'#DBEAFE' },
            { v: module.completed_issues ?? 0, label:'Done',    color:'#16A34A', bg:'#DCFCE7' },
          ].map(s => (
            <div key={s.label}
              style={{ background:s.bg, color:s.color, borderRadius:5, padding:'1px 7px',
                       fontSize:'0.67rem', fontWeight:700, display:'flex', alignItems:'center', gap:3 }}>
              {s.v} <span style={{ fontWeight:500, opacity:0.75 }}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Modules (main) ────────────────────────────────────────────── */
export default function Modules({ project }) {
  const [modules,     setModules]     = useState([]);
  const [members,     setMembers]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showCreate,  setShowCreate]  = useState(false);
  const [editTarget,  setEditTarget]  = useState(null);
  const [openModule,  setOpenModule]  = useState(null);

  const projectId = project.id;

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      pm.getModules(projectId),
      pm.getMembers(projectId),
    ]).then(([mRes, memRes]) => {
      setModules(mRes.data);
      setMembers(memRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (mod) => {
    if (!window.confirm(`Delete module "${mod.name}"? Issues will not be deleted.`)) return;
    await pm.deleteModule(mod.id).catch(() => {});
    load();
  };

  /* ── If a module is open, show detail view ── */
  if (openModule) {
    const fresh = modules.find(m => m.id === openModule.id) || openModule;
    return (
      <ModuleDetail
        projectId={projectId}
        module={fresh}
        members={members}
        onBack={() => { setOpenModule(null); load(); }}
        onUpdated={load}
      />
    );
  }

  /* ── Group by status ── */
  const grouped = MODULE_STATUS_OPTIONS.reduce((acc, s) => {
    acc[s] = modules.filter(m => m.status === s);
    return acc;
  }, {});

  const nonEmpty = MODULE_STATUS_OPTIONS.filter(s => grouped[s].length > 0);

  const StatusSection = ({ statusKey }) => {
    const sm    = MODULE_STATUS[statusKey];
    const items = grouped[statusKey];
    return (
      <div>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.75rem', padding:'0 0.25rem' }}>
          <span style={{ width:7, height:7, borderRadius:'50%', background:sm.color, flexShrink:0 }} />
          <span style={{ fontSize:'0.82rem', fontWeight:700, color:'#374151' }}>{sm.label}</span>
          <span style={{ fontSize:'0.72rem', color:'#9CA3AF', fontWeight:500 }}>({items.length})</span>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:'0.75rem' }}>
          {items.map(m => (
            <ModuleCard key={m.id} module={m}
              onOpen={() => setOpenModule(m)}
              onEdit={() => setEditTarget(m)}
              onDelete={() => handleDelete(m)} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
    {(showCreate || editTarget) && (
      <ModuleFormModal
        projectId={projectId}
        module={editTarget}
        members={members}
        onSave={() => { setShowCreate(false); setEditTarget(null); load(); }}
        onClose={() => { setShowCreate(false); setEditTarget(null); }}
      />
    )}

    <div style={{ padding:'1.25rem 1.5rem', display:'flex', flexDirection:'column', gap:'1.5rem' }}>
      {/* Toolbar */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <h2 style={{ margin:0, fontSize:'1rem', fontWeight:700, color:'#111827' }}>Modules</h2>
          <p style={{ margin:'0.15rem 0 0', fontSize:'0.78rem', color:'#9CA3AF' }}>
            Group issues by feature areas or work streams
          </p>
        </div>
        <button onClick={() => setShowCreate(true)}
          style={{ padding:'0.45rem 1.1rem', border:'none', borderRadius:8,
                   background:theme.primary, color:'#fff', cursor:'pointer',
                   fontSize:'0.82rem', fontWeight:700, display:'flex', alignItems:'center', gap:'0.35rem' }}>
          + New Module
        </button>
      </div>

      {loading ? (
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center',
                      justifyContent:'center', gap:'0.75rem', padding:'5rem 0', color:'#9CA3AF' }}>
          <div style={{ width:32, height:32, border:'3px solid #E4E4E7',
                        borderTopColor:theme.primary, borderRadius:'50%',
                        animation:'spin 0.8s linear infinite' }} />
          <span style={{ fontSize:'0.82rem' }}>Loading modules…</span>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      ) : modules.length === 0 ? (
        <div style={{ textAlign:'center', padding:'5rem 0' }}>
          <div style={{ fontSize:'3rem', marginBottom:'0.75rem', opacity:0.3 }}>▦</div>
          <p style={{ fontWeight:700, color:'#111827', margin:'0 0 0.25rem' }}>No modules yet</p>
          <p style={{ color:'#9CA3AF', fontSize:'0.82rem', margin:'0 0 1.25rem' }}>
            Group related issues into modules to track feature-level progress.
          </p>
          <button onClick={() => setShowCreate(true)}
            style={{ padding:'0.5rem 1.25rem', border:'none', borderRadius:8,
                     background:theme.primary, color:'#fff', cursor:'pointer',
                     fontSize:'0.85rem', fontWeight:700 }}>
            Create First Module
          </button>
        </div>
      ) : nonEmpty.length === 0 ? null : (
        nonEmpty.map(s => <StatusSection key={s} statusKey={s} />)
      )}
    </div>
    </>
  );
}
